/**
 * Progress — статусы (Cadet → Imperator) и ранги (Lunar → Cosmic).
 *
 * Показывает текущий прогресс юзера по двум системам:
 * - Статусы: 20 уровней, накопительный оборот сети
 * - Ранги: 5 уровней, скользящие 30 дней первой линии
 *
 * Данные пока мок — подключатся через useDashboardUser() позже.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, type Variants } from 'motion/react';
import { Trophy, Crown, Lock, Check, ArrowLeft } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import {
  GlassSurface,
  GlassPill,
  ClickableCard,
  InfoTooltip,
} from '@/components/glass';

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { delayChildren: 0.05, staggerChildren: 0.06 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.32, 0.72, 0, 1] } },
};

// ── Данные статусов (ТЗ секция 06) ──────────────────────────────
// По ТЗ у каждого статуса ДВА условия: личный объём + накопительный
// оборот сети. Оба порога должны быть выполнены. Синхронизировано с
// таблицей luna_status_config (migration 005).
interface StatusLevel {
  level: number;
  name: string;
  personalReq: number;
  networkReq: number;
  bonus: number;     // разовый бонус USDT
}

const STATUSES: StatusLevel[] = [
  { level: 1,  name: 'Cadet',      personalReq: 100,     networkReq: 500,        bonus: 7.5 },
  { level: 2,  name: 'Pilot',      personalReq: 300,     networkReq: 1_500,      bonus: 22.5 },
  { level: 3,  name: 'Navigator',  personalReq: 500,     networkReq: 3_000,      bonus: 45 },
  { level: 4,  name: 'Commander',  personalReq: 1_000,   networkReq: 7_000,      bonus: 105 },
  { level: 5,  name: 'Captain',    personalReq: 2_000,   networkReq: 15_000,     bonus: 225 },
  { level: 6,  name: 'Sentinel',   personalReq: 3_000,   networkReq: 25_000,     bonus: 500 },
  { level: 7,  name: 'Warden',     personalReq: 5_000,   networkReq: 40_000,     bonus: 800 },
  { level: 8,  name: 'Vanguard',   personalReq: 7_000,   networkReq: 60_000,     bonus: 1_200 },
  { level: 9,  name: 'Centurion',  personalReq: 10_000,  networkReq: 100_000,    bonus: 2_000 },
  { level: 10, name: 'Tribune',    personalReq: 15_000,  networkReq: 150_000,    bonus: 3_000 },
  { level: 11, name: 'Consul',     personalReq: 20_000,  networkReq: 250_000,    bonus: 6_250 },
  { level: 12, name: 'Prefect',    personalReq: 30_000,  networkReq: 400_000,    bonus: 10_000 },
  { level: 13, name: 'Arbiter',    personalReq: 50_000,  networkReq: 600_000,    bonus: 15_000 },
  { level: 14, name: 'Legate',     personalReq: 75_000,  networkReq: 1_000_000,  bonus: 25_000 },
  { level: 15, name: 'Praetor',    personalReq: 100_000, networkReq: 2_000_000,  bonus: 60_000 },
  { level: 16, name: 'Proconsul',  personalReq: 150_000, networkReq: 4_000_000,  bonus: 120_000 },
  { level: 17, name: 'Senator',    personalReq: 250_000, networkReq: 7_000_000,  bonus: 210_000 },
  { level: 18, name: 'Chancellor', personalReq: 500_000, networkReq: 12_000_000, bonus: 360_000 },
  { level: 19, name: 'Sovereign',  personalReq: 750_000, networkReq: 18_000_000, bonus: 540_000 },
  { level: 20, name: 'Imperator',  personalReq: 1_000_000, networkReq: 20_000_000, bonus: 600_000 },
];

// ── Ранги (ТЗ секция 07): 5 уровней, оборот L1 за 30 дней ──────
interface RankLevel {
  level: number;
  name: string;
  turnover30d: number;
  bonus: number;
}

const RANKS: RankLevel[] = [
  { level: 1, name: 'Lunar',    turnover30d: 5_000,   bonus: 150 },
  { level: 2, name: 'Stellar',  turnover30d: 15_000,  bonus: 525 },
  { level: 3, name: 'Orbital',  turnover30d: 30_000,  bonus: 1_200 },
  { level: 4, name: 'Galactic', turnover30d: 60_000,  bonus: 2_700 },
  { level: 5, name: 'Cosmic',   turnover30d: 100_000, bonus: 5_000 },
];

export function Progress() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const navigate = useNavigate();
  const [currentStatusLevel, setCurrentStatusLevel] = useState(0);
  const [networkTurnover, setNetworkTurnover] = useState(0);
  const [personalDeposit, setPersonalDeposit] = useState(0);
  const [currentRankLevel, setCurrentRankLevel] = useState(0);
  const [firstLineTurnover30d, setFirstLineTurnover30d] = useState(0);

  // ЕДИНЫЙ источник истины — эндпоинт /me/progress. Он читает
  // awarded-статусы из luna_user_statuses (а не «qualifies»), поэтому
  // чекмарки совпадают с реальностью. Ранг — current_rank из state,
  // оборот сети — с maxDepth=10 (все линии) по ТЗ.
  useEffect(() => {
    import('@/services/api').then(({ lunaApi }) => {
      lunaApi.progress.snapshot().then((res) => {
        const p = res.data?.progress;
        if (!p) return;
        setCurrentStatusLevel(p.status.currentLevel);
        setCurrentRankLevel(p.rank.currentLevel);
        // Оборот для прогресс-бара следующего статуса (network)
        if (p.status.network) {
          setNetworkTurnover(parseFloat(p.status.network.current));
        }
        // Личный — для двойной проверки «qualifies»
        if (p.status.personal) {
          setPersonalDeposit(parseFloat(p.status.personal.current));
        }
        // L1 оборот за 30 дней для ранга
        if (p.rank.l1Turnover30d) {
          setFirstLineTurnover30d(parseFloat(p.rank.l1Turnover30d.current));
        }
      }).catch(() => {});
    });
  }, []);
  return (
    <motion.div
      className="space-y-5 max-w-md mx-auto pt-2 pb-24"
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      {/* ── BACK BUTTON ── */}
      <motion.div variants={itemVariants}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="lg-pill !rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 text-[12px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] active:scale-95 transition"
        >
          <ArrowLeft size={14} strokeWidth={2} />
          {t('progress.back')}
        </button>
      </motion.div>

      {/* ── СТАТУСЫ ── */}
      <motion.div variants={itemVariants}>
        <GlassSurface variant="strong" blur className="p-5 relative">
          <InfoTooltip
            tone="nebula"
            className="!top-[10px] !right-[10px]"
            ariaLabel={t('progress.status.tooltipAria')}
            title={t('progress.status.tooltipTitle')}
            body={<Trans i18nKey="progress.status.tooltipBody" components={{ b: <strong />, br: <br /> }} />}
            bullets={[
              t('progress.status.bullet1'),
              t('progress.status.bullet2'),
              t('progress.status.bullet3'),
              t('progress.status.bullet4'),
            ]}
          />
          <div className="flex items-center gap-2.5 mb-4">
            <div
              className="h-9 w-9 rounded-xl flex items-center justify-center"
              style={{
                background: 'color-mix(in oklab, var(--accent-tertiary) 14%, transparent)',
                boxShadow: 'inset 0 0 0 1px color-mix(in oklab, var(--accent-tertiary) 30%, transparent)',
              }}
            >
              <Trophy size={18} strokeWidth={1.8} style={{ color: 'var(--accent-tertiary)' }} />
            </div>
            <div>
              <h2 className="font-display text-[16px] font-extrabold tracking-tight text-[var(--text-primary)]">
                {t('progress.status.title')}
              </h2>
              <p className="text-[11px] text-[var(--text-muted)]">
                {t('progress.status.subtitle')}
              </p>
            </div>
          </div>

          {/* Пояснение: личный объём и оборот сети — два разных
              независимых показателя, не складываются. Это критично,
              потому что юзеры регулярно путают пороги (Sentinel:
              ≥ 3 000 личный И ≥ 25 000 сеть, не сумма). */}
          <p
            className="text-[11px] leading-[1.5] font-mono mb-3 px-2.5 py-2 rounded-lg"
            style={{
              color: 'var(--text-secondary)',
              background:
                'color-mix(in oklab, var(--status-warning) 8%, transparent)',
              border:
                '1px solid color-mix(in oklab, var(--status-warning) 22%, transparent)',
            }}
          >
            {t('progressBars.personalVsNetworkNote')}
          </p>

          <div className="space-y-2">
            {STATUSES.map((s) => {
              const achieved = s.level <= currentStatusLevel;
              const next = s.level === currentStatusLevel + 1;
              // По ТЗ статус требует ОБА условия: personal >= personalReq
              // И network >= networkReq. Прогресс = минимум (узкое место).
              const personalPct = Math.min(100, (personalDeposit / s.personalReq) * 100);
              const networkPct = Math.min(100, (networkTurnover / s.networkReq) * 100);
              const progress = next
                ? Math.round(Math.min(personalPct, networkPct))
                : achieved ? 100 : 0;

              return (
                <div
                  key={s.level}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                  style={{
                    background: achieved
                      ? 'color-mix(in oklab, var(--accent-tertiary) 6%, transparent)'
                      : next
                        ? 'color-mix(in oklab, var(--accent-primary) 4%, transparent)'
                        : 'transparent',
                    border: next ? '1px solid color-mix(in oklab, var(--accent-primary) 12%, transparent)' : '1px solid transparent',
                  }}
                >
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      background: achieved
                        ? 'var(--accent-tertiary)'
                        : 'color-mix(in oklab, var(--text-muted) 10%, transparent)',
                    }}
                  >
                    {achieved ? (
                      <Check size={13} strokeWidth={3} className="text-[#0a0a0f]" />
                    ) : (
                      <Lock size={11} strokeWidth={2} className="text-[var(--text-muted)]" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-[13px] font-semibold ${achieved ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
                        {s.name}
                      </span>
                      <span className="text-[11px] text-[var(--text-muted)]">
                        ${s.networkReq.toLocaleString(locale)}
                      </span>
                    </div>
                    {next && (
                      <div className="mt-1.5 space-y-1.5">
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--glass-fill)' }}>
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${progress}%`,
                              background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-secondary))',
                            }}
                          />
                        </div>
                        <div className="flex flex-col gap-0.5 text-[10px] text-[var(--text-muted)] leading-tight">
                          <span>
                            Личный: <span style={{ color: personalDeposit >= s.personalReq ? 'var(--accent-secondary)' : 'var(--text-secondary)' }}>
                              ${personalDeposit.toLocaleString(locale)} / ${s.personalReq.toLocaleString(locale)}
                            </span>
                          </span>
                          <span>
                            Сеть: <span style={{ color: networkTurnover >= s.networkReq ? 'var(--accent-secondary)' : 'var(--text-secondary)' }}>
                              ${networkTurnover.toLocaleString(locale)} / ${s.networkReq.toLocaleString(locale)}
                            </span>
                          </span>
                          <span className="text-[var(--accent-primary)] mt-0.5">
                            Бонус: +${s.bonus.toLocaleString(locale)} USDT
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </GlassSurface>
      </motion.div>

      {/* ── РАНГИ ── */}
      <motion.div variants={itemVariants}>
        <GlassSurface variant="strong" blur className="p-5 relative">
          <InfoTooltip
            tone="lunar"
            className="!top-[10px] !right-[10px]"
            ariaLabel={t('progress.rank.tooltipAria')}
            title={t('progress.rank.tooltipTitle')}
            body={<Trans i18nKey="progress.rank.tooltipBody" components={{ b: <strong />, br: <br /> }} />}
            bullets={[
              t('progress.rank.bullet1'),
              t('progress.rank.bullet2'),
              t('progress.rank.bullet3'),
              t('progress.rank.bullet4'),
            ]}
          />
          <div className="flex items-center gap-2.5 mb-4">
            <div
              className="h-9 w-9 rounded-xl flex items-center justify-center"
              style={{
                background: 'color-mix(in oklab, var(--accent-primary) 14%, transparent)',
                boxShadow: 'inset 0 0 0 1px color-mix(in oklab, var(--accent-primary) 30%, transparent)',
              }}
            >
              <Crown size={18} strokeWidth={1.8} style={{ color: 'var(--accent-primary)' }} />
            </div>
            <div>
              <h2 className="font-display text-[16px] font-extrabold tracking-tight text-[var(--text-primary)]">
                {t('progress.rank.title')}
              </h2>
              <p className="text-[11px] text-[var(--text-muted)]">
                {t('progress.rank.subtitle')}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {RANKS.map((r) => {
              const achieved = r.level <= currentRankLevel;
              const next = r.level === currentRankLevel + 1;
              const progress = next
                ? Math.min(100, Math.round((firstLineTurnover30d / r.turnover30d) * 100))
                : achieved ? 100 : 0;

              return (
                <ClickableCard
                  key={r.level}
                  className="px-3 py-3"
                  style={{
                    background: achieved
                      ? 'color-mix(in oklab, var(--accent-primary) 6%, transparent)'
                      : 'transparent',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        background: achieved
                          ? 'var(--accent-primary)'
                          : 'color-mix(in oklab, var(--text-muted) 10%, transparent)',
                      }}
                    >
                      {achieved ? (
                        <Check size={13} strokeWidth={3} className="text-[#0a0a0f]" />
                      ) : (
                        <Lock size={11} strokeWidth={2} className="text-[var(--text-muted)]" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-[13px] font-semibold ${achieved ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
                            {r.name}
                          </span>
                          {achieved && (
                            <GlassPill tone="positive" compact>{t('progress.rank.active')}</GlassPill>
                          )}
                        </div>
                        <span className="text-[11px] text-[var(--text-muted)]">
                          ${r.turnover30d.toLocaleString(locale)}
                        </span>
                      </div>
                      {next && (
                        <div className="mt-1.5">
                          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--glass-fill)' }}>
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{
                                width: `${progress}%`,
                                background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-secondary))',
                              }}
                            />
                          </div>
                          <p className="text-[10px] text-[var(--text-muted)] mt-1">
                            {t('progress.status.progressLabel', { current: firstLineTurnover30d.toLocaleString(locale), threshold: r.turnover30d.toLocaleString(locale), bonus: r.bonus })}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </ClickableCard>
              );
            })}
          </div>

          <p className="text-[10px] text-[var(--text-muted)] mt-3 text-center">
            {t('progress.rank.footer')}
          </p>
        </GlassSurface>
      </motion.div>
    </motion.div>
  );
}
